import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { PairingLookupResponse } from '@brewcore/app-contract';
import {
  checkTypedCode,
  defaultMachineName,
  pairingErrorText,
  t,
  type Lang,
  type PairingApi,
} from '../core/index.js';
import {
  Banner,
  Button,
  Checkbox,
  Chip,
  CodeCells,
  DisplayHint,
  Gauge,
  Icon,
  MachineArt,
  Sheet,
  Spinner,
  TextField,
} from './components/index.js';

type Step = 'enter' | 'confirm' | 'waiting' | 'done';

const significant = (raw: string) => raw.toUpperCase().replace(/[^0-9A-Z]/g, '');

/**
 * Pairing as a full screen flow (FR-6, AD-43): code from the display first (or the token of the QR link), then
 * the question that names the target account with the machine name and, for a reinstallation, the offer to
 * continue the old machine; waiting while the API pairs; success.
 */
export function PairingFlow({
  lang,
  api,
  initialToken,
  onClose,
  onPaired,
}: {
  lang: Lang;
  api: PairingApi;
  initialToken: string | null;
  onClose: () => void;
  onPaired: (machineId: string) => void;
}) {
  const [step, setStep] = useState<Step>('enter');
  const [code, setCode] = useState('');
  const [input, setInput] = useState<{ code?: string; token?: string } | null>(null);
  const [info, setInfo] = useState<PairingLookupResponse | null>(null);
  const [name, setName] = useState('');
  const [continueOld, setContinueOld] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [scanHelp, setScanHelp] = useState(false);
  const [pairedName, setPairedName] = useState('');
  const codeField = useRef<HTMLInputElement>(null);

  const lookup = async (what: { code?: string; token?: string }) => {
    setBusy(true);
    setError('');
    const r = await api.lookup(what);
    setBusy(false);
    if (!r.ok) {
      setError(pairingErrorText(lang, r.error));
      setStep('enter');
      return;
    }
    setInput(what);
    setInfo(r.value);
    setName(r.value.reinstall?.name ?? defaultMachineName(r.value.type));
    setContinueOld(r.value.reinstall !== null && !r.value.reinstall.recentlyConnected);
    setStep('confirm');
  };

  useEffect(() => {
    if (initialToken) void lookup({ token: initialToken });
    // only once for the token of the QR link
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step === 'enter') codeField.current?.focus();
  }, [step]);

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    const normalized = checkTypedCode(code);
    if (!normalized) {
      setError(t(lang, 'pairCodeFormat'));
      return;
    }
    void lookup({ code: normalized });
  };

  const submitConfirm = async (e: FormEvent) => {
    e.preventDefault();
    if (!input || !info) return;
    setBusy(true);
    setError('');
    setStep('waiting');
    const r = await api.confirm({
      ...input,
      ...(name.trim() ? { name: name.trim() } : {}),
      ...(continueOld && info.reinstall ? { continueFrom: info.reinstall.machineId } : {}),
    });
    setBusy(false);
    if (!r.ok) {
      setError(pairingErrorText(lang, r.error));
      setStep('confirm');
      return;
    }
    setPairedName(r.value.name);
    setStep('done');
    onPaired(r.value.machineId);
  };

  const stepOf = step === 'enter' ? 1 : step === 'confirm' ? 2 : null;
  const errorBanner = error && (
    <Banner tone="danger" icon="alert" role="alert" data-testid="pair-error">
      {error}
    </Banner>
  );

  return (
    <Sheet
      variant="full"
      title={t(lang, 'pair')}
      titleId="pair-title"
      onClose={onClose}
      closeLabel={step === 'done' ? undefined : t(lang, 'cancel')}
      closeTestId="pair-cancel"
      headerAside={
        stepOf ? (
          <span className="pair-step num">
            <span className="pair-step-long">{t(lang, 'pairStep', { n: stepOf, m: 2 })}</span>
            <span className="pair-step-short" aria-hidden="true">
              {t(lang, 'pairStepShort', { n: stepOf, m: 2 })}
            </span>
          </span>
        ) : undefined
      }
    >
      {step === 'enter' && (
        <form onSubmit={submitCode} className="pair-screen">
          <div className="pair-display">
            <DisplayHint label={t(lang, 'pairDisplayLabel')} />
          </div>
          <p className="pair-lead">{t(lang, 'pairCodeIntro')}</p>
          <CodeCells
            id="pair-code"
            ref={codeField}
            data-testid="pair-code"
            label={t(lang, 'pairCodeLabel')}
            groups={[4, 4]}
            significant={significant}
            value={code}
            onChange={setCode}
            info={t(lang, 'pairCodeInfo')}
            infoLabel={t(lang, 'infoShow')}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            inputMode="text"
            maxLength={12}
          />
          {errorBanner}
          {/* right under the code, not at the screen's bottom edge (board "Maschine koppeln"); the camera as a button */}
          <div className="pair-actions is-inline">
            <Button
              type="submit"
              block
              busy={busy}
              disabled={busy || code.trim() === ''}
              data-testid="pair-next"
            >
              {busy ? t(lang, 'pairWorking') : t(lang, 'pairNext')}
            </Button>
            <Button
              variant="secondary"
              block
              icon="scan"
              aria-expanded={scanHelp}
              aria-controls="pair-scan-help"
              onClick={() => setScanHelp((v) => !v)}
            >
              {t(lang, 'pairScan')}
            </Button>
            {scanHelp && (
              <p id="pair-scan-help" className="pair-scan-help">
                <Icon name="qr" />
                <span>{t(lang, 'pairScanHelp')}</span>
              </p>
            )}
          </div>
        </form>
      )}

      {step === 'confirm' && info && (
        <form onSubmit={(e) => void submitConfirm(e)} className="pair-screen">
          <div className="pair-found">
            <MachineArt />
            <Chip tone="ok" icon="check">
              {t(lang, 'pairFound')}
            </Chip>
            <h3 className="title-lg">{defaultMachineName(info.type)}</h3>
            <p className="hint">{t(lang, 'pairFirmware', { fw: info.fw })}</p>
          </div>
          <p className="pair-question" data-testid="pair-question">
            {t(lang, 'pairQuestion', { account: info.account.email })}
          </p>
          {info.blocked ? (
            <Banner tone="danger" icon="alert" role="alert" data-testid="pair-blocked">
              {t(lang, 'pairBlocked')}
            </Banner>
          ) : (
            <>
              <TextField
                id="pair-name"
                data-testid="pair-name"
                label={t(lang, 'pairName')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
              />
              {info.reinstall && !info.reinstall.recentlyConnected && (
                <Checkbox
                  id="pair-continue"
                  data-testid="pair-continue"
                  checked={continueOld}
                  onChange={(e) => setContinueOld(e.target.checked)}
                  label={t(lang, 'pairReinstall', { name: info.reinstall.name })}
                />
              )}
              {info.reinstall?.recentlyConnected && (
                <p className="hint">{t(lang, 'pairReinstallRecent', { name: info.reinstall.name })}</p>
              )}
            </>
          )}
          {errorBanner}
          <div className="pair-actions">
            <Button type="submit" block busy={busy} disabled={busy || info.blocked} data-testid="pair-submit">
              {busy ? t(lang, 'pairWorking') : t(lang, 'pairSubmit')}
            </Button>
            <Button variant="ghost" block onClick={() => setStep('enter')} disabled={busy}>
              {t(lang, 'pairBack')}
            </Button>
          </div>
        </form>
      )}

      {step === 'waiting' && (
        <div className="pair-screen pair-center" aria-live="polite">
          <div className="pair-ring">
            <Gauge spinning tone="heat" ticks={false}>
              <Icon name="machine" size="lg" className="gauge-icon" />
            </Gauge>
          </div>
          <h3 className="title-lg">
            {t(lang, 'pairWaitingTitle', { name: name.trim() || defaultMachineName(info?.type ?? '') })}
          </h3>
          <ul className="checklist">
            <li className="is-done">
              <Icon name="check" size="sm" />
              {t(lang, 'pairWaitCode')}
            </li>
            <li className="is-now">
              <Spinner size="sm" />
              {t(lang, 'pairWaitAccount')}
            </li>
          </ul>
          <p className="hint">{t(lang, 'pairWaitHint')}</p>
        </div>
      )}

      {step === 'done' && (
        <div className="pair-screen pair-center">
          <div className="pair-ring">
            <Gauge full tone="ready">
              <Icon name="check" size="xl" className="gauge-icon" />
            </Gauge>
          </div>
          <h3 className="title-xl">{t(lang, 'pairDoneTitle')}</h3>
          <p className="text-dim" data-testid="pair-done">
            {t(lang, 'pairDone', { name: pairedName })}
          </p>
          <div className="pair-actions">
            <Button block onClick={onClose} data-testid="pair-close">
              {t(lang, 'pairToMachine')}
            </Button>
          </div>
        </div>
      )}
    </Sheet>
  );
}
